import { useState, useEffect } from "react";
import get from "lodash/get";
import { useSelector } from "react-redux";
import { getCollectionitems } from "../../../../api/utils";
import { collectionToStoriesWithForumFilter } from "../../../utils/forum-filter";

export const useCategoryLandingLogic = (props) => {
  const member = useSelector((state) => get(state, ["member"], null));
  const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));
  const isAuthInitializing = useSelector((state) => get(state, ["isAuthInitializing"], false));

  // Return loading state if member exists but subscriberMetadata is not available yet
  const isLoadingMetadata = member && !subscriberMetadata;

  const collection = get(props, ["data", "collection"], {});

  // State for managing all fetched stories and pagination
  const [allFetchedStories, setAllFetchedStories] = useState(collection.items || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Apply client-side forum filtering to all fetched stories
  const filteredStories = collectionToStoriesWithForumFilter({ items: allFetchedStories }, member, subscriberMetadata) || [];

  const STORIES_PER_PAGE = 12;
  const STORIES_PER_PAGE_AFTER_BRAND = STORIES_PER_PAGE - 1;

  // Use filtered stories count instead of server count
  const totalStories = filteredStories.length;

  const totalPages =
    totalStories <= 11
      ? 1
      : Math.ceil((totalStories - 11) / STORIES_PER_PAGE_AFTER_BRAND) + 1;

  const [mobile, setMobile] = useState(false);    
  const [cardsPerRow, setCardsPerRow] = useState(3);
  const [hideImages, setHideImages] = useState(false); 
  const [mounted, setMounted] = useState(false);  

  useEffect(() => {
    const updateViewport = () => {
      const width = window.innerWidth;

      setMobile(width < 768);

      if (width >= 1024) setCardsPerRow(3);
      else if (width >= 768) setCardsPerRow(2);
      else setCardsPerRow(1);

      setHideImages(width < 768);
    };

    updateViewport();
    setMounted(true);

    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  // Effect to fetch more stories when needed
  useEffect(() => {
    const fetchStories = async () => {
      if (isLoadingMore) return;

      const totalServerStories = collection["total-count"] || 0;
      const currentStoriesEndIndex = currentPage === 1 ? 11 : 11 + (currentPage - 1) * STORIES_PER_PAGE_AFTER_BRAND;

      // If we need more stories and haven't loaded all available stories from server
      if (currentStoriesEndIndex >= filteredStories.length && allFetchedStories.length < totalServerStories) {
        setIsLoadingMore(true);
        try {
          const newOffset = allFetchedStories.length;
          const batchSize = 11; // Fetch larger batches to account for filtering
          const newItems = await getCollectionitems(collection.slug, newOffset, batchSize);
          if (newItems && newItems.length > 0) {
            setAllFetchedStories(prev => [...prev, ...newItems]);
          }
        } catch (error) {
          console.error('Error fetching more stories:', error);
        } finally {
          setIsLoadingMore(false);
        }
      }
    };

    fetchStories();
  }, [currentPage]); // Only depend on currentPage to avoid infinite loops

  // Calculate current page stories from filtered results
  let currentPageStories = [];

  if (currentPage === 1) {
    currentPageStories = filteredStories.slice(0, 11);
  } else {
    const start = 11 + (currentPage - 2) * STORIES_PER_PAGE_AFTER_BRAND;
    const end = start + STORIES_PER_PAGE_AFTER_BRAND;
    currentPageStories = filteredStories.slice(start, end);
  }

  const handlePageChange = async (pageNumber) => {
    setCurrentPage(pageNumber); // The useEffect will handle fetching more stories if needed
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getStoryCount = () => {
    const unit = totalStories === 1 ? "Report" : "Reports";
    return `${totalStories} ${unit}`;
  };

  const chunkStories = (storiesArray, chunkSize = 3) => {
    const chunks = [];
    for (let i = 0; i < storiesArray.length; i += chunkSize) {
      chunks.push(storiesArray.slice(i, i + chunkSize));
    }
    return chunks;
  };

  return {
    collection,
    stories: filteredStories,
    totalStories,
    totalPages,
    currentPage,
    currentPageStories,
    cardsPerRow,
    hideImages,
    handlePageChange,
    getStoryCount,
    chunkStories,
    isMobile: () => mobile,
    mounted,
    isLoadingMetadata,
    isLoadingMore,
    isAuthInitializing
  };
};
